<template>
  <DialogWrapper
    :persistent="true"
    :dialog="dialog"
    overlayColor="#3776CB"
    @close-dialog="$emit('close-dialog')"
    :title="$t('sharing.title')"
  >
    <v-row class="main-share-dialog">
      <v-col align-self="center" cols="12" sm="3">
        <div class="main first-letter-capital">
          {{$t('sharing.label')}}
          <v-tooltip bottom max-width="300">
            <template v-slot:activator="{ on }">
              <span class="helper ml-3" v-on="on">
                <v-icon x-small color="white">mdi-help</v-icon>
              </span>
            </template>
            <span class="first-letter-capital">{{$t('sharing.help')}}</span>
          </v-tooltip>
        </div>
      </v-col>
      <v-col align-self="center" cols="12" sm="9">
        <v-combobox
          v-model="chips"
          :hint="$t('sharing.hint')"
          persistent-hint
          chips
          hide-details
          append-icon
          clearable
          :delimiters="[' ']"
          :rules="emailRules"
          multiple
        >
          <template v-slot:selection="{ attrs, item, select, selected, index }">
            <v-chip
              v-bind="attrs"
              small
              :color="errorChipsColor[index]"
              :outlined="!!errorChipsColor[index]"
              :input-value="selected"
              @click="select"
              close
              @click:close="remove(item)"
            >
              <strong>{{ item }}</strong>&nbsp;
            </v-chip>
          </template>
        </v-combobox>
      </v-col>
    </v-row>
    <AlertRow
      v-if="invalidEmailMessages"
      :message="invalidEmailMessages && invalidEmailMessages.message"
      :level="invalidEmailMessages && invalidEmailMessages.level"
      :noPadding="true"
    >
    {{invalidEmailMessages && invalidEmailMessages.details}}
    </AlertRow>
    <template v-slot:actions>
      <div class="share-scn-btn-div">
        <v-btn
          :class="{'disable-events': isDisabled }"
          dark
          x-large
          rounded
          :color="isDisabled ? buttonDisabledColor : buttonColor"
          class="text-none share-scn-btn"
          @click.stop="sendScenario"
        >{{$t('sharing.action')}}</v-btn>
      </div>
    </template>
  </DialogWrapper>
</template>

<script lang='ts'>
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import _color from '@/helpers/colors';
import AlertRow from '@/components/AlertRow.vue';
import DialogWrapper from '@/components/DialogWrapper.vue';

@Component({
  components: {
    DialogWrapper,
    AlertRow
  }
})
export default class SharingDialog extends Vue {
  @Prop({ default: () => false })
  private dialogProp!: boolean;
  @Prop({ default: () => false })
  private hasBeenShared!: boolean;
  @Prop({ default: () => null })
  private invalidMails!: string[] | null;

  private dialog: boolean = false;
  private chips: string[] = [];
  private email: string | null = '';
  private emailMatchingString = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;
  private emailRules = [
    (v: any) => {
      if (!v || v.length < 1) return 'Email is required';
      else if (v.length > 0) {
        for (let i = 0; i < v.length; i++) {
          if (!this.emailMatchingString.test(v[i])) {
            return 'Invalid email';
          }
        }
      }
      return true;
    }
  ];
  // TODO : A getter with API response
  private buttonColor: string = _color.pinkButton;
  private errorColor: string = _color['vv-red'];
  private buttonDisabledColor: string = _color.pinkDisabledButton;

  get errorChipsColor(): any {
    const r = (v: any) => this.emailMatchingString.test(v);
    const arr = [];
    for (const c of this.chips) {
      if (r(c)) {
        arr.push(undefined);
      } else {
        arr.push(this.errorColor);
      }
    }
    return arr;
  }
  get isDisabled(): boolean {
    if (!this.errorChipsColor.length) {
      return true;
    }
    if (this.errorChipsColor.filter((e: any) => e !== undefined).length) {
      return true;
    }
    return false;
  }
  get isChecking(): boolean {
    const tmp = this.$store.getters['scenario/getIsMailChecking'];
    return tmp;
  }
  get invalidEmailMessages() {
    if (!this.invalidMails) {
      return null;
    }
    if (this.invalidMails && this.invalidMails.length === 0) {
      return {
        message: this.$t('sharing.success'), // 'You have shared this scenario',
        level: 'success'
      };
    }
    return {
      message: this.$t('sharing.error'), // 'Those mails are not stored in our database',
      details: this.invalidMails && this.invalidMails
    };
  }

  private remove(item: any) {
    this.chips.splice(this.chips.indexOf(item), 1);
    this.chips = [...this.chips];
  }

  private closeHandler() {
    this.$emit('close-dialog');
  }

  private checkIfEmailIsOk() {
    this.$store.dispatch('scenario/checkMail');
  }
  private sendScenario() {
    this.$emit('send-scenario', this.chips);
  }

  @Watch('dialogProp')
  private assignDialog(value: boolean) {
    this.dialog = this.dialogProp;
  }
  @Watch('email')
  private getHasChanged() {
    // here we will set the send button disable
    // if the mail field has been modified
  }
  // @Watch('invalidMails')
  // private handleMails() {

  // }
}
</script>

<style lang='stylus' scoped>
@import '../stylus/main.styl';

.main {
  color: #1F489B;
}

.helper {
  background-color: #1F489B;
  position: relative;
  padding: 0px;
  width: 15px;
  height: 15px;
  display: inline-block;
  border-radius: 50%;
  text-align: center;
  vertical-align: middle;
  opacity: 0.4;

  i {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 30px;
  }
}

.sharing-dialog {
  background-color: white !important;
}

.share-scn-btn-div {
  width: 100%;
}

.share-scn-btn {
  left: 50%;
  transform: translateX(-50%);
}

.dialog-title {
  color: $deep-blue;
}

.disable-events {
  pointer-events: none;
}
</style>
